<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>友情链接</h1>
        <p>展示在用户端「关于」页的合作方小程序，点一下直接跳过去</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增链接
        </el-button>
      </div>
    </div>

    <!-- 跳转前提说明：这是运营最容易踩的坑 —— 拿公众号/网页链接来配 -->
    <div class="hint-bar link-tip">
      <i class="el-icon-warning-outline" />
      <span>
        跳转依赖<b>对方小程序的 AppID</b>（以 wx 开头）。公众号、H5 网页链接在这里都跳不过去。
      </span>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <!-- 链接主体：logo + 名称 + 说明，这一列是整张表的信息重心 -->
        <el-table-column label="链接" min-width="320">
          <template slot-scope="{ row }">
            <div class="link-cell">
              <div class="link-logo" :class="{ 'link-logo--empty': !row.logo }">
                <img v-if="row.logo" :src="row.logo" alt="">
                <i v-else class="el-icon-picture-outline" />
              </div>
              <div class="link-main">
                <div class="cell-main">{{ row.name }}</div>
                <div class="cell-sub">{{ row.description || '未填写说明' }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="合作时间" width="140">
          <template slot-scope="{ row }">
            <span v-if="row.coopTime" class="chip-time">{{ row.coopTime }}</span>
            <span v-else class="cell-sub">—</span>
          </template>
        </el-table-column>

        <el-table-column label="跳转目标" min-width="200">
          <template slot-scope="{ row }">
            <div class="appid">{{ row.appId }}</div>
            <div class="cell-sub path-text">{{ row.path || '对方首页' }}</div>
          </template>
        </el-table-column>

        <el-table-column label="排序" width="80" align="center">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ row.sortOrder }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="row.isActive === 1 ? 's1' : 's0'">
              <i />{{ row.isActive === 1 ? '展示中' : '已隐藏' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <el-dropdown trigger="click" @command="cmd => handleAction(cmd, row)">
                <span class="act act-more">
                  操作<i class="el-icon-arrow-down" />
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item command="toggle">
                    <i :class="row.isActive === 1 ? 'el-icon-view' : 'el-icon-open'" />
                    {{ row.isActive === 1 ? '下架' : '上架' }}
                  </el-dropdown-item>
                  <el-dropdown-item command="edit">
                    <i class="el-icon-edit" /> 编辑
                  </el-dropdown-item>
                  <el-dropdown-item command="delete" divided>
                    <i class="el-icon-delete" /> 删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </div>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="link-empty">
            <p>还没有友情链接</p>
            <span>点右上角「新增链接」，把合作方的小程序挂上去</span>
          </div>
        </template>
      </el-table>
    </div>

    <!-- ── 新增 / 编辑弹窗 ─────────────────────────────────── -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="620px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="92px">
        <el-form-item label="链接名称" prop="name">
          <el-input v-model="form.name" maxlength="50" show-word-limit placeholder="对方小程序的名称" />
        </el-form-item>

        <!-- Logo：左侧预览即时反馈，右侧上传 —— 运营改图不用先传完再想 -->
        <el-form-item label="Logo">
          <div class="logo-field">
            <div class="logo-preview" :class="{ 'logo-preview--empty': !form.logo }">
              <img v-if="form.logo" :src="form.logo" alt="">
              <i v-else class="el-icon-picture-outline" />
            </div>
            <div class="logo-actions">
              <el-button size="small" class="ghost-btn" :loading="uploading" @click="pickLogo">
                {{ form.logo ? '重新上传' : '上传图片' }}
              </el-button>
              <el-button v-if="form.logo" size="small" class="ghost-btn" @click="form.logo = ''">
                移除
              </el-button>
              <div class="field-note">建议正方形，浅色背景更容易和列表协调</div>
            </div>
          </div>
          <input ref="logoInput" type="file" accept="image/*" class="hidden-input" @change="onLogoChange">
        </el-form-item>

        <el-form-item label="说明">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="2"
            maxlength="200"
            show-word-limit
            placeholder="一句话说清对方是做什么的"
          />
        </el-form-item>

        <el-form-item label="合作时间">
          <el-input v-model="form.coopTime" maxlength="30" placeholder="如 2024.01 至今（纯展示，随便写）" />
        </el-form-item>

        <el-form-item label="小程序 AppID" prop="appId">
          <el-input v-model="form.appId" maxlength="64" placeholder="以 wx 开头，如 wx1234567890abcdef" />
          <div class="field-note">用户点击后跳转到该小程序。填错只会跳转失败，不会报错。</div>
        </el-form-item>

        <el-form-item label="跳转路径">
          <el-input v-model="form.path" maxlength="255" placeholder="留空进入对方首页，如 /pages/index/index" />
          <div class="field-note">带参数也可以，如 /pages/detail/detail?id=1</div>
        </el-form-item>

        <el-form-item label="排序">
          <div class="sort-field">
            <el-input-number v-model="form.sortOrder" :min="0" :max="9999" size="small" controls-position="right" />
            <span class="field-note field-note--inline">数字越小越靠前，用户端按这个顺序排</span>
          </div>
        </el-form-item>

        <el-form-item label="上架状态">
          <el-switch v-model="form.isActive" :active-value="1" :inactive-value="0" active-text="展示" inactive-text="隐藏" />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" :loading="saving" @click="handleSubmit">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getFriendLinkList,
  createFriendLink,
  updateFriendLink,
  toggleFriendLink,
  deleteFriendLink
} from '@/api/friend-link'
import { uploadImage } from '@/api/upload'

/** 表单初始值。放在常量里，重置时不会漏字段（漏一个就会"新增时带着上一条的值"） */
const EMPTY_FORM = () => ({
  id: null,
  name: '',
  logo: '',
  description: '',
  coopTime: '',
  appId: '',
  path: '',
  sortOrder: 0,
  isActive: 1
})

/** 图片大小上限：logo 用不到更大的图，卡在这里免得有人传 20MB 的原图 */
const MAX_LOGO_SIZE = 5 * 1024 * 1024

export default {
  name: 'FriendLink',
  data() {
    return {
      list: [],
      listLoading: false,
      dialogVisible: false,
      dialogTitle: '新增友情链接',
      saving: false,
      uploading: false,
      form: EMPTY_FORM(),
      rules: {
        name: [{ required: true, message: '请填写链接名称', trigger: 'blur' }],
        appId: [
          { required: true, message: '请填写对方小程序的 AppID', trigger: 'blur' },
          { pattern: /^wx[0-9a-zA-Z]{16}$/, message: 'AppID 通常形如 wx + 16 位字符', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    fetchList() {
      this.listLoading = true
      getFriendLinkList().then(res => {
        this.list = (res.data || [])
        this.listLoading = false
      }).catch(() => {
        this.listLoading = false
      })
    },
    handleCreate() {
      this.dialogTitle = '新增友情链接'
      this.form = EMPTY_FORM()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.dialogTitle = '编辑友情链接'
      this.form = {
        id: row.id,
        name: row.name,
        logo: row.logo || '',
        description: row.description || '',
        coopTime: row.coopTime || '',
        appId: row.appId,
        path: row.path || '',
        sortOrder: row.sortOrder != null ? row.sortOrder : 0,
        isActive: row.isActive != null ? row.isActive : 1
      }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const payload = { ...this.form }
        const isEdit = !!payload.id
        const request = isEdit ? updateFriendLink(payload.id, payload) : createFriendLink(payload)
        this.saving = true
        request.then(() => {
          this.saving = false
          this.$message.success(isEdit ? '已保存' : '新增成功')
          this.dialogVisible = false
          this.fetchList()
        }).catch(() => {
          // 每条分支都要复位，否则按钮会一直转圈（请求失败也一样）
          this.saving = false
        })
      })
    },
    handleAction(command, row) {
      if (command === 'edit') {
        this.handleEdit(row)
        return
      }
      if (command === 'toggle') {
        const off = row.isActive === 1
        this.$confirm(
          off
            ? `「${row.name}」将从用户端「关于」页隐藏，随时可以再上架。`
            : `「${row.name}」将展示在用户端「关于」页。`,
          off ? '下架链接' : '上架链接',
          { type: 'warning' }
        ).then(() => {
          toggleFriendLink(row.id).then(() => {
            this.$message.success(off ? '已下架' : '已上架')
            this.fetchList()
          }).catch(() => {})
        }).catch(() => {})
        return
      }
      if (command === 'delete') {
        this.$confirm(
          `删除后「${row.name}」将从列表和用户端同时消失，且无法恢复。`,
          '删除链接',
          { type: 'warning', confirmButtonText: '删除', confirmButtonClass: 'el-button--danger' }
        ).then(() => {
          deleteFriendLink(row.id).then(() => {
            this.$message.success('已删除')
            this.fetchList()
          }).catch(() => {})
        }).catch(() => {})
      }
    },
    pickLogo() {
      this.$refs.logoInput.click()
    },
    onLogoChange(e) {
      const file = e.target.files && e.target.files[0]
      // 先清空 value：同一个文件连选两次也要能触发 change
      e.target.value = ''
      if (!file) return
      if (file.type.indexOf('image/') !== 0) {
        this.$message.error('请选择图片文件')
        return
      }
      if (file.size > MAX_LOGO_SIZE) {
        this.$message.error('图片不要超过 5MB')
        return
      }
      this.uploading = true
      uploadImage(file, 'FRIEND_LINK_LOGO').then(res => {
        this.uploading = false
        this.form.logo = (res.data && res.data.url) || ''
        this.$message.success('上传成功')
      }).catch(() => {
        this.uploading = false
      })
    }
  }
}
</script>

<style scoped>
.link-tip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 16px;
  line-height: 1.7;
}

.link-tip i {
  margin-top: 2px;
  flex: 0 0 auto;
}

/* ===== 列表 ===== */
.link-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.link-logo {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: var(--paper-100, #f4ece6);
  display: flex;
  align-items: center;
  justify-content: center;
}

.link-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.link-logo--empty i {
  font-size: 20px;
  color: var(--ink-300, #a79ba9);
}

.link-main {
  min-width: 0;
}

.chip-time {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: var(--paper-100, #f4ece6);
  color: var(--ink-500, #6b5f70);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.appid {
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 12.5px;
  color: var(--ink-900, #171219);
  word-break: break-all;
}

.path-text {
  font-family: 'SFMono-Regular', Consolas, monospace;
  word-break: break-all;
}

.link-empty {
  padding: 26px 0 30px;
}

.link-empty p {
  font-size: 14px;
  color: var(--ink-700, #3d3444);
  margin: 0 0 6px;
}

.link-empty span {
  font-size: 12.5px;
  color: var(--ink-400, #8c8191);
}

/* ===== 弹窗 ===== */
.logo-field {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.logo-preview {
  width: 76px;
  height: 76px;
  border-radius: 18px;
  overflow: hidden;
  flex-shrink: 0;
  background-color: var(--paper-100, #f4ece6);
  border: 1px dashed var(--ink-200, #cfc6d1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.logo-preview--empty i {
  font-size: 24px;
  color: var(--ink-300, #a79ba9);
}

.logo-actions {
  padding-top: 2px;
}

.hidden-input {
  display: none;
}

.field-note {
  font-size: 12px;
  color: var(--ink-400, #8c8191);
  line-height: 1.6;
  margin-top: 6px;
}

.field-note--inline {
  margin-top: 0;
}

.sort-field {
  display: flex;
  align-items: center;
  gap: 12px;
}
</style>
